import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { forensicService } from '../../services/api';
import {
    FileText,
    Clock,
    User,
    Search,
    Plus,
    Download,
    ShieldCheck,
    Database,
    FileSearch,
    ChevronDown,
    ChevronUp,
    Info,
    CheckCircle2,
    DatabaseZap,
    ExternalLink,
    Trash2,
    Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { ForensicIngestionModal } from './ForensicIngestionModal';

// Native Intl avoids the date-fns dependency.
const _DATE_FMT = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
});
const formatDate = (s: string) => {
    try { return _DATE_FMT.format(new Date(s)); }
    catch { return s; }
};

interface ForensicEvidenceSectionProps {
    caseId: string;
}

export const ForensicEvidenceSection: React.FC<ForensicEvidenceSectionProps> = ({ caseId }) => {
    const navigate = useNavigate();
    const [records, setRecords] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const loadRecords = async () => {
        setIsLoading(true);
        try {
            const data = await forensicService.getRecords(caseId);
            setRecords(data);
        } catch (error) {
            console.error('Failed to load forensic records', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadRecords();
    }, [caseId]);

    const toggleExpand = (id: string) => {
        setExpandedRecordId(expandedRecordId === id ? null : id);
    };

    const handleDelete = async (record: any) => {
        // Destructive: cascade-deletes all verified_tx_links rows owned by this
        // record (FK ON DELETE CASCADE). After delete the user can re-upload
        // the same file without hitting file_hash dedup.
        const linkCount = record.links_count ?? 0;
        const confirmMsg =
            `Delete "${record.file_name}"?\n\n` +
            `This will remove the record + ${linkCount} link${linkCount === 1 ? '' : 's'} from the canvas overlay.\n` +
            `You can then re-upload the same file (or an updated version).\n\n` +
            `This cannot be undone.`;
        if (!window.confirm(confirmMsg)) return;
        setDeletingId(record.id);
        try {
            const result = await forensicService.deleteRecord(caseId, record.id);
            toast.success(`Deleted ${result.file_name} (${result.links_deleted} links removed)`);
            await loadRecords();
        } catch (error: any) {
            toast.error(error?.response?.data?.detail || 'Failed to delete forensic record');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-bold text-foreground">Forensic Evidence Locker</h3>
                        <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded-full"
                            title="Deterministic, subpoena-grade evidence. Every uploaded file is SHA-256 hashed on its real bytes (chain of custody) and parsed into verified cross-chain transaction links rendered on the workbench canvas. Tier 1 in the evidence model, including Tier-1a proof anchors (gold edges where both the on-chain hash and the counterparty are known)."
                        >
                            <ShieldCheck className="w-3 h-3" />
                            Subpoena-grade · Tier 1
                        </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 max-w-[440px]">
                        Cryptographically hashed subpoena records — upload exchange KYC/transaction responses; every file is SHA-256 hashed for chain of custody and parsed into verified deterministic transaction links shown on the canvas.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {records.length > 0 && (
                        <>
                            <button
                                onClick={() => navigate(`/cases/${caseId}/macro`)}
                                className="flex items-center gap-1.5 px-3 h-9 bg-primary text-primary-foreground hover:bg-brand-gold-hover font-bold text-sm rounded-md transition-all"
                                title="Operator-level overview: cluster wallets, run signals, see chain spread"
                            >
                                <FileSearch className="w-4 h-4" />
                                Open Overview
                            </button>
                            <button
                                onClick={() => navigate(`/workbench?case_id=${caseId}`)}
                                className="flex items-center gap-1.5 px-3 h-9 bg-primary/10 text-foreground hover:bg-primary/20 font-bold text-sm rounded-md transition-all border border-primary/30"
                                title="Render this case's forensic evidence on the Workbench canvas"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Open in Workbench
                            </button>
                        </>
                    )}
                    <button
                        onClick={() => setIsUploadModalOpen(true)}
                        className="flex items-center gap-2 px-4 h-9 bg-primary text-primary-foreground font-bold text-sm rounded-md hover:opacity-90 transition-all shadow-sm"
                    >
                        <Plus className="w-4 h-4" />
                        Ingest Record
                    </button>
                </div>
            </div>

            {records.length === 0 && !isLoading ? (
                <div className="border-2 border-dashed border-border/40 rounded-xl bg-secondary/5 p-10 text-center flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-muted-foreground/40" />
                    </div>
                    <div className="space-y-1">
                        <p className="text-sm font-bold">No forensic records yet</p>
                        <p className="text-xs text-muted-foreground max-w-[300px] mx-auto">
                            Upload exchange responses to unlock deterministic cross-chain tracing and KYC evidence.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-3">
                    {records.map((record) => {
                        const isExpanded = expandedRecordId === record.id;
                        const summary = record.metadata?.ingest_summary;

                        return (
                            <div key={record.id} className={`group bg-card border ${isExpanded ? 'border-blue-500' : 'border-border'} rounded-lg overflow-hidden transition-all shadow-sm`}>
                                <div className="p-4 flex items-start gap-4">
                                    <div className="p-2.5 rounded-lg bg-blue-500/10 text-blue-500 group-hover:bg-blue-500/20 transition-colors">
                                        <ShieldCheck className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1 space-y-1.5 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h4 className="font-bold text-sm truncate">{record.file_name}</h4>
                                            <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 text-[9px] font-black uppercase tracking-tighter border border-blue-500/20">
                                                {record.record_type?.replace('_', ' ') || 'RECORD'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4 text-[10px] text-muted-foreground font-medium">
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3 opacity-60" />
                                                {formatDate(record.created_at)}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <User className="w-3 h-3 opacity-60" />
                                                Officer #{record.created_by || 'System'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {/* Live link count chip — distinct from frozen movements_total in metadata.
                                            Lets the user see the result of the cleanup / Tier-1a promotions. */}
                                        <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold text-emerald-500 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded mr-1" title="Live count of verified_tx_links rows">
                                            <Database className="w-3 h-3" />
                                            {record.links_count ?? 0} links
                                        </span>
                                        <button
                                            onClick={() => toggleExpand(record.id)}
                                            className={`p-2 rounded-md transition-all ${isExpanded ? 'bg-blue-500/10 text-blue-500' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                                            title="Forensic Audit"
                                        >
                                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <Info className="w-4 h-4" />}
                                        </button>
                                        <button
                                            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-all"
                                            title="Download Original"
                                        >
                                            <Download className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(record)}
                                            disabled={deletingId === record.id}
                                            className="p-2 text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10 rounded-md transition-all disabled:opacity-50 disabled:hover:bg-transparent"
                                            title="Delete record + cascade verified_tx_links (enables clean re-upload)"
                                        >
                                            {deletingId === record.id
                                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                                : <Trash2 className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="border-t border-border bg-muted/30 p-4 animate-in slide-in-from-top-2 duration-200">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Metrics — bound to the actual ingest_summary fields the
                                                Binance/Freeform parsers emit (see services/forensic/ingestion_service.py). */}
                                            <div className="space-y-4">
                                                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                                                    <DatabaseZap className="w-3 h-3 text-blue-500" />
                                                    Ingestion Metrics
                                                </p>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className="bg-background/50 border border-border/50 p-2.5 rounded-lg" title="Frozen snapshot from the parser at upload time">
                                                        <p className="text-[10px] text-muted-foreground uppercase font-bold">Parsed Movements</p>
                                                        <p className="text-lg font-bold text-foreground tabular-nums">{summary?.movements_total ?? '—'}</p>
                                                    </div>
                                                    <div className="bg-blue-500/5 border border-blue-500/20 p-2.5 rounded-lg" title="Current verified_tx_links row count (updates after cleanup / re-ingest)">
                                                        <p className="text-[10px] text-blue-400 uppercase font-bold">Live Links in DB</p>
                                                        <p className="text-lg font-bold text-blue-400 tabular-nums">{record.links_count ?? 0}</p>
                                                    </div>
                                                    <div className="bg-emerald-500/5 border border-emerald-500/20 p-2.5 rounded-lg">
                                                        <p className="text-[10px] text-emerald-500/80 uppercase font-bold">With On-Chain Hash</p>
                                                        <p className="text-lg font-bold text-emerald-500 tabular-nums">{summary?.movements_with_hash ?? 0}</p>
                                                    </div>
                                                    {(summary?.movements_internal_id_only ?? 0) > 0 && (
                                                        <div className="bg-amber-500/5 border border-amber-500/20 p-2.5 rounded-lg">
                                                            <p className="text-[10px] text-amber-500/80 uppercase font-bold">Internal-ID Only</p>
                                                            <p className="text-lg font-bold text-amber-500 tabular-nums">{summary.movements_internal_id_only}</p>
                                                        </div>
                                                    )}
                                                    {(summary?.unsupported_rows_count ?? 0) > 0 && (
                                                        <div className="bg-rose-500/5 border border-rose-500/20 p-2.5 rounded-lg">
                                                            <p className="text-[10px] text-rose-500/80 uppercase font-bold">Unsupported Rows</p>
                                                            <p className="text-lg font-bold text-rose-500 tabular-nums">{summary.unsupported_rows_count}</p>
                                                        </div>
                                                    )}
                                                </div>
                                                {summary?.movements_total !== undefined && (record.links_count ?? 0) !== summary.movements_total && (
                                                    <p className="text-[10px] text-muted-foreground italic leading-snug">
                                                        Live count differs from parsed: {summary.movements_total - (record.links_count ?? 0)} link{Math.abs(summary.movements_total - (record.links_count ?? 0)) === 1 ? '' : 's'} {(record.links_count ?? 0) < summary.movements_total ? 'removed by cleanup or admin action' : 'added (likely Tier-1a promotion)'}.
                                                    </p>
                                                )}

                                                {summary?.customer && (summary.customer.name || summary.customer.email || summary.customer.exchange_user_id) && (
                                                    <div className="bg-background/50 border border-border/50 p-2.5 rounded-lg space-y-1">
                                                        <p className="text-[10px] text-muted-foreground uppercase font-bold mb-1">Customer (KYC)</p>
                                                        {summary.customer.name && <p className="text-xs font-bold text-foreground">{summary.customer.name}</p>}
                                                        {summary.customer.email && <p className="text-[11px] text-blue-400 break-all">{summary.customer.email}</p>}
                                                        {summary.customer.exchange_user_id && (
                                                            <p className="text-[10px] text-muted-foreground">
                                                                {summary.customer.exchange_name || 'Exchange'} UID: <span className="font-mono text-foreground">{summary.customer.exchange_user_id}</span>
                                                            </p>
                                                        )}
                                                    </div>
                                                )}

                                                {record.metadata?.notes && (
                                                    <div className="bg-blue-500/5 border-l-2 border-blue-500/30 p-2.5 rounded-r">
                                                        <p className="text-[11px] text-blue-400 font-bold uppercase tracking-tight mb-1">Officer Notes</p>
                                                        <p className="text-[11px] italic text-muted-foreground">"{record.metadata.notes}"</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Source structure + warnings */}
                                            <div className="space-y-4">
                                                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                                                    <FileSearch className="w-3 h-3 text-blue-500" />
                                                    Source Structure
                                                </p>

                                                <div className="space-y-3">
                                                    <div>
                                                        <p className="text-[10px] font-bold text-emerald-500/80 uppercase mb-1.5 flex items-center gap-1.5">
                                                            <CheckCircle2 className="w-3 h-3" /> Sheets Parsed
                                                        </p>
                                                        <div className="flex flex-wrap gap-1">
                                                            {summary?.sheets?.length ? summary.sheets.map((s: string) => (
                                                                <span key={s} className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 rounded text-[9px] font-medium">
                                                                    {s}
                                                                </span>
                                                            )) : <span className="text-[10px] text-muted-foreground italic">None recorded</span>}
                                                            {summary?.parser && (
                                                                <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded text-[9px] font-bold uppercase">
                                                                    {summary.parser} parser
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {summary?.warnings?.length > 0 && (
                                                        <div>
                                                            <p className="text-[10px] font-bold text-amber-500/80 uppercase mb-1.5 flex items-center gap-1.5">
                                                                <Clock className="w-3 h-3" /> Parser Warnings ({summary.warnings.length})
                                                            </p>
                                                            <div className="space-y-1 max-h-40 overflow-y-auto">
                                                                {summary.warnings.slice(0, 15).map((w: string, i: number) => (
                                                                    <p key={i} className="text-[10px] text-amber-400/80 leading-snug bg-amber-500/5 border-l-2 border-amber-500/30 px-1.5 py-1 rounded-r">
                                                                        {w}
                                                                    </p>
                                                                ))}
                                                                {summary.warnings.length > 15 && (
                                                                    <p className="text-[9px] text-muted-foreground self-center ml-1">
                                                                        +{summary.warnings.length - 15} more
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <ForensicIngestionModal 
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                caseId={caseId}
                onSuccess={loadRecords}
            />
        </div>
    );
};

// Add missing Calendar icon locally
const Calendar = ({ className }: { className?: string }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className={className}
    >
        <rect width="18" height="18" x="3" y="4" rx="2" ry="2" /><line x1="16" x2="16" y1="2" y2="6" /><line x1="8" x2="8" y1="2" y2="6" /><line x1="3" x2="21" y1="10" y2="10" />
    </svg>
);
